import * as React from "react";
import Container from "@amzn/awsui-components-react/polaris/container";
import Header from "@amzn/awsui-components-react/polaris/header";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Box from "@amzn/awsui-components-react/polaris/box";
import Button from "@amzn/awsui-components-react/polaris/button";
import Tabs from "@amzn/awsui-components-react/polaris/tabs";
import Alert from "@amzn/awsui-components-react/polaris/alert";
import type { MedicationOption } from "../../../update_pharmacy_customer/components/prescription/components/add_prescription/PrescriptionDetailsBlock";
import { NewRxItemEntry, EMPTY_ITEM } from "../../../update_pharmacy_customer/components/prescription/components/add_prescription_newrx/NewRxItemTypes";
import NewRxItemTabContent from "../../../update_pharmacy_customer/components/prescription/components/add_prescription_newrx/NewRxItemTabContent";

export interface NewRxItemsSectionProps {
  /** The recipe's NewRx (PMGS) batch items. Empty means "no NewRx". */
  items: NewRxItemEntry[];
  /** Which tab is visible. */
  activeTabIndex: number;
  /** Medications for the per-item drug Autosuggest (shared, loaded once by the parent). */
  medications: MedicationOption[];
  /** eScribe greys the editor: its Rx is a fixed, verified med — NewRx is not combinable with it. */
  disabled: boolean;
  /** Drug IDs that also appear in the legacy medication list — flagged inline (the backend rejects the overlap). */
  conflictingDrugIds?: string[];
  /** True when items exist but the recipe's inter-step wait is below the NewRx floor — surfaces a warning. */
  waitTooLow?: boolean;
  /** The NewRx wait floor (seconds), shown in the warning copy. */
  minWaitSeconds?: number;
  onItemsChange: (items: NewRxItemEntry[]) => void;
  onActiveTabChange: (index: number) => void;
}

/**
 * NewRx (PMGS) batch editor for the CPCP recipe Medications step. Each tab is one NewRxBatchItem, authored
 * with the reused standalone NewRx blocks (NewRxItemTabContent → prescriber/medication/stopAtStep). Controlled:
 * the parent (RecipeBuilder) owns the items array + active tab and the final recipe emission/validation.
 *
 * Mutually exclusive with the legacy medication list only at the drugId level — that cross-list check lives in
 * the parent (it needs both lists). Here we just manage the item tabs. Under eScribe the whole editor is
 * replaced with an explanatory Alert, since NewRx cannot combine with the eScribe ingestion path.
 */
export default class NewRxItemsSection extends React.Component<NewRxItemsSectionProps> {
  private addTab = () => {
    const { items, onItemsChange, onActiveTabChange } = this.props;
    onItemsChange([...items, { ...EMPTY_ITEM }]);
    onActiveTabChange(items.length);
  };

  private removeTab = (index: number) => {
    const { items, activeTabIndex, onItemsChange, onActiveTabChange } = this.props;
    const next = items.filter((_, i) => i !== index);
    onItemsChange(next);
    const nextActive =
      activeTabIndex >= next.length ? Math.max(0, next.length - 1) : activeTabIndex > index ? activeTabIndex - 1 : activeTabIndex;
    onActiveTabChange(nextActive);
  };

  private updateEntry = (updated: NewRxItemEntry) => {
    const { items, activeTabIndex, onItemsChange } = this.props;
    // Clamp to match the index render uses (activeIndex), so an out-of-range activeTabIndex can't write
    // to the wrong slot or past the end of the array.
    const idx = Math.min(activeTabIndex, items.length - 1);
    const next = [...items];
    next[idx] = updated;
    onItemsChange(next);
  };

  render() {
    const { items, activeTabIndex, medications, disabled } = this.props;

    const header = (
      <Header
        variant="h3"
        description="PMGS NewRx batch — prescriptions ingested for the created customer (asynchronous)"
      >
        NewRx Prescriptions (Optional)
      </Header>
    );

    if (disabled) {
      return (
        <Container header={header}>
          <Alert type="info" header="Not applicable for eScribe">
            eScribe is its own ingestion path (a fixed, verified medication). NewRx (PMGS) prescriptions cannot be
            combined with it — turn off eScribe to add NewRx items.
          </Alert>
        </Container>
      );
    }

    if (items.length === 0) {
      return (
        <Container header={header}>
          <SpaceBetween size="s">
            <Box color="text-body-secondary">No NewRx prescriptions. Add one to ingest it via PMGS.</Box>
            <Button iconName="add-plus" onClick={this.addTab}>
              Add NewRx prescription
            </Button>
          </SpaceBetween>
        </Container>
      );
    }

    const activeIndex = Math.min(activeTabIndex, items.length - 1);
    const conflictSet = new Set(this.props.conflictingDrugIds || []);
    const isConflict = (item: NewRxItemEntry) =>
      item.drugSource === "drugId" && !!item.drugId.trim() && conflictSet.has(item.drugId.trim());
    const activeConflict = isConflict(items[activeIndex]);

    return (
      <Container header={header}>
        <SpaceBetween size="l">
          <Tabs
            activeTabId={String(activeIndex)}
            onChange={({ detail }) => this.props.onActiveTabChange(Number(detail.activeTabId))}
            tabs={items.map((item, i) => ({
              id: String(i),
              label:
                (isConflict(item) ? "⚠ " : "") +
                (item.drugIdInput || `Prescription ${i + 1}`).replace(/\s*\(.*\)$/, ""),
              // Always dismissible — removing the last item returns to the empty "Add" state, so a user who
              // added a prescription by mistake can back all the way out.
              dismissible: true,
              dismissLabel: `Remove Prescription ${i + 1}`,
              onDismiss: () => this.removeTab(i),
            }))}
            actions={<Button iconName="add-plus" variant="icon" onClick={this.addTab} />}
          />

          {activeConflict && (
            <Alert type="error" header="Drug is in both lists">
              This drug is also in the medication list. The two lists cannot share a drug — remove it from one of
              them.
            </Alert>
          )}

          {this.props.waitTooLow && (
            <Alert type="warning" header="Step Wait Seconds too low for NewRx">
              NewRx prescriptions ingest asynchronously, so the recipe's inter-step wait must be at least{" "}
              {this.props.minWaitSeconds ?? 300}s for the batch to finish before CPCP validates. Raise Step Wait
              Seconds on the New Type step before saving.
            </Alert>
          )}

          <NewRxItemTabContent entry={items[activeIndex]} medications={medications} onChange={this.updateEntry} />
        </SpaceBetween>
      </Container>
    );
  }
}
